import { PointOfInterest, PointOfInterestTemplateTypeEnum } from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
import { useGroupBy } from '@/hooks';
import { GroupByResult, PointOfInterestGroupBy } from '@/types';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { AllEquipmentBackButton } from '../../modules/pointOfInterest';
import { PointsOfInterestList } from './PointsOfInterestList';
import { PoiTypeAccordion } from './PoiTypeAccordion';
import { SearchBar } from './SearchBar';
import { Container, ScrollableContainer } from './styles';
import { PoiTypeAccordionSkeleton } from './PoiTypeAccordion/PoiTypeAccordionSkeleton';
import { Stack, Typography } from '@mui/material';
import { abyssColors } from '@/theme/colors';

export const PointsOfInterestByType = ({
  pois,
  changeTab,
  allPOILoading,
}: {
  pois: PointOfInterest[];
  changeTab: (selectedTabIndex: number) => void;
  allPOILoading?: boolean;
}) => {
  const allTemplates = useAtomValue(analysisState.allPointOfInterestTemplates);
  const selectedSpherical = useAtomValue(analysisState.selectedSpherical);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredGroupedPois, setFilteredGroupedPois] = useState<
    GroupByResult<PointOfInterestGroupBy>
  >({});

  const groupedPois = useGroupBy<PointOfInterestGroupBy>(pois, 'templateId');

  useEffect(() => {
    setFilteredGroupedPois(groupedPois);
  }, [groupedPois]);

  const handleSearchPoi = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    if (!searchTerm) {
      setFilteredGroupedPois(groupedPois);
      return;
    }

    const updatedGroupedPois = Object.keys(groupedPois).reduce((accumulator, templateId) => {
      const filteredPois = groupedPois[templateId].filter(
        (poi) =>
          (poi.poiId ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (poi.name ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (poi.assembly?.tagName ?? '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      return { ...accumulator, [templateId]: filteredPois };
    }, {});
    setFilteredGroupedPois(updatedGroupedPois);
  };

  const filteredTemplates = useMemo(
    () =>
      allTemplates.filter(
        (template) =>
          template.type !== PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest &&
          filteredGroupedPois[template.id]?.length > 0
      ),
    [allTemplates, filteredGroupedPois]
  );

  const isLoading = useMemo(
    () => allPOILoading || allTemplates.length === 0,
    [allPOILoading, allTemplates]
  );

  return (
    <Container>
      <AllEquipmentBackButton
        retainAssemblyView={!!selectedSpherical}
        changeTab={changeTab}
        sx={{ margin: '1.6rem 0' }}
      />
      <SearchBar
        value={searchTerm}
        onChange={handleSearchPoi}
        disabled={isLoading || pois.length === 0}
      />
      {isLoading && <PoiTypeAccordionSkeleton />}
      {!isLoading && Object.keys(filteredTemplates).length > 0 && (
        <ScrollableContainer>
          {filteredTemplates.map((template) => (
            <PoiTypeAccordion
              key={template.id}
              title={template.name}
              details={
                <PointsOfInterestList
                  pointsOfInterest={filteredGroupedPois[template.id] ?? []}
                  type={template.name}
                />
              }
              defaultExpanded={false}
              count={filteredGroupedPois[template.id]?.length ?? 0}
            />
          ))}
        </ScrollableContainer>
      )}
      {!isLoading && (pois.length === 0 || Object.keys(filteredTemplates).length === 0) && (
        <Stack width="100%" alignItems="center" justifyContent="center" marginTop="3.2rem">
          <Typography variant="body2" color={abyssColors.neutral[400]}>
            No Points of Interest Found
          </Typography>
        </Stack>
      )}
    </Container>
  );
};
